import memoize from 'fast-memoize';

import { getThumbBySize } from '../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';

/**
 * Given an AlbumMembershipGroup identity all entitled photo ids.
 *
 * @param {Object} albumMembershipGroupIdentity The AlbumMembershipGroup identity.
 * @returns {Array}
 */
export const getEntitledPhotoIds = memoize(albumMembershipGroupIdentity => {
  if (!albumMembershipGroupIdentity) {
    return [];
  }

  return albumMembershipGroupIdentity.entitled_matched_photos.records.map(
    photo => photo.id,
  );
});

/**
 * Checks whether a matched photo is entitled by cross referencing
 * its id on the entitled photo ids.
 *
 * @param {Object} albumMembershipGroupIdentity The AlbumMembershipGroup identity.
 * @param {string} matchedPhotoId The matched photo id.
 * @returns {boolean}
 */
export const isPhotoEntitledById = (
  albumMembershipGroupIdentity,
  matchedPhotoId,
) => {
  const entitledPhotoIds = getEntitledPhotoIds(albumMembershipGroupIdentity);

  return entitledPhotoIds.includes(matchedPhotoId);
};

export const hasNonEntitledPhotos = albumMembershipGroupIdentity => {
  if (!albumMembershipGroupIdentity) {
    return false;
  }

  const entitledPhotoIds = getEntitledPhotoIds(albumMembershipGroupIdentity);

  return (
    entitledPhotoIds.length <
    albumMembershipGroupIdentity.matched_photos_extended.records.length
  );
};

/**
 * Checks whether the memberships cart contains any cart item for
 * a particular photo (by photo id).
 *
 * @param {Array<Object>} cart The cart.
 * @param {string} photoId The photo id.
 * @returns {boolean}
 */
export const membershipsCartContainsPhoto = (cart = [], photoId) => {
  return cart.some(item => item?.photo?.id === photoId);
};

/**
 * Retrieves the first matched photo URL of an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {string}
 */
export const getAlbumMembershipGroupPreviewMatchedPhotoUrl =
  albumMembershipGroup => {
    if (!albumMembershipGroup) {
      return '';
    }

    const [matchedPhoto] =
      albumMembershipGroup.entitled_matched_photos?.records ?? [];

    return (
      getThumbBySize(
        matchedPhoto?.photo?.thumbnail_urls,
        IMAGE_SIZE_CODE.MEDIUM2X,
      )?.url ?? ''
    );
  };

export const isMatchedPhotoPpaAvailable = matchedPhoto => {
  if (!matchedPhoto) {
    return false;
  }

  return (
    !matchedPhoto?.has_purchased_album_identity &&
    matchedPhoto?.album?.price_whole_album?.value > 0
  );
};

/**
 * Whether the matches view must be hidden for the hierarchy item, regardless
 * of the matches the viewer has. Folder items resolve from the folder itself,
 * album items from their root AlbumFolder.
 *
 * @param {Object} params
 * @param {Object} [params.albumFolder] The hierarchy item AlbumFolder.
 * @param {Object} [params.album] The hierarchy item Album.
 * @returns {boolean}
 */
export const isMatchesViewHidden = ({ albumFolder, album } = {}) =>
  !!(
    albumFolder?.hide_matches_view ??
    albumFolder?.root?.hide_matches_view ??
    album?.root_album_folder?.hide_matches_view
  );
